import { NgTemplateOutlet } from "@angular/common";
import { ChangeDetectionStrategy, Component, ElementRef, computed, effect, input, signal, viewChild } from "@angular/core";

import { AnalyticsClickDirective, ClickAreaDirective } from "@directives";
import { InfoType, MenuTab } from "@models";
import { TranslatePipe } from "@pipes";

import { AbstractTabComponent } from "../abstract/abstract-tab.component";
import { ButtonComponent } from "../button/button.component";
import { SvgComponent } from "../svg/svg.component";

interface Article {
  text: string;
  element: HTMLElement;
}

@Component({
  selector: "mn-info-tab",
  templateUrl: "./info-tab.component.html",
  styleUrls: ["./info-tab.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonComponent, SvgComponent, AnalyticsClickDirective, TranslatePipe, NgTemplateOutlet, ClickAreaDirective],
})
export class InfoTabComponent extends AbstractTabComponent {
  public type = input.required<InfoType>();

  public aE = viewChild.required<ElementRef>("aE");
  public cE = viewChild.required<ElementRef>("cE");

  protected readonly mult = computed<string>(() => this.acS.iS$()!.max_multiplier!);
  protected readonly rtp = computed<number>(() => this.acS.gSB$()!.rtp!);

  protected readonly aA = signal<Article | null>(null);

  protected readonly ars = signal<Article[]>([]);
  protected readonly I_T = InfoType;

  constructor() {
    super();

    effect(onCleanup => {
      const element = this.cE();

      if (!element) {
        return;
      }

      this.articles();

      const observer = new MutationObserver(() => this.articles());

      observer.observe(element.nativeElement, {
        childList: true,
        subtree: true,
        characterData: true,
      });

      onCleanup(() => observer.disconnect());
    });
  }

  protected override close(event: Event, back: boolean = false): void {
    event.stopPropagation();

    this.acS.mS$.set(back ? { tab: this.type() === InfoType.F ? MenuTab.F : MenuTab.M } : null);
  }

  protected select(article: Article, tabEl: HTMLElement): void {
    this.aA.set(article);

    const contentEl = this.cE().nativeElement;

    this.aE().nativeElement.scrollTo({
      left: tabEl.offsetLeft - contentEl.offsetLeft - contentEl.clientWidth / 2 + tabEl.offsetWidth / 2,
      behavior: "smooth",
    });
    contentEl.scrollTo({ top: article.element.offsetTop - 120, behavior: "smooth" });
  }

  private articles(): void {
    const articles = Array.from(this.cE().nativeElement.querySelectorAll("b[data-article]") as NodeListOf<HTMLElement>).map(article => ({
      element: article,
      text: article.innerText.replace(/[:?]/g, ""),
    }));

    this.ars.set(articles);
    this.aA.set(articles[0]);
  }
}
